import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography } from "@mui/material";
import { TrendChartCard } from "./TrendChartCard";
import { LivestreamViewerDualLineChart } from "./LivestreamViewerDualLineChart";
import { calculateMedian, explorerAccentAt, formatNumber } from "./utils";

export type LivestreamWeeklyTrendRow = {
  week: string;
  weekStart: string;
  views: number;
  viewsPerStream: number;
  avgViewers: number;
  peakViewers: number;
  streamCount: number;
};

export type LivestreamWeeklyTrendsSectionProps = {
  trends: LivestreamWeeklyTrendRow[];
  isLoading?: boolean;
};

export const LivestreamWeeklyTrendsSection: React.FC<
  LivestreamWeeklyTrendsSectionProps
> = ({ trends, isLoading = false }) => {
  const { t } = useTranslation("explorer");
  const data = trends ?? [];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {t("weeklyTrends")}
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <TrendChartCard
          title={t("views")}
          dataKey="views"
          color={explorerAccentAt(0)}
          tooltipLabel={t("views")}
          granularityTooltip={t("granularity.week")}
          data={data}
          median={
            data.length > 0 && !isLoading
              ? calculateMedian(
                  data
                    .map((t) => t.views)
                    .filter((v): v is number => typeof v === "number"),
                )
              : undefined
          }
          formatValue={formatNumber}
          yAxisWidth={45}
          isLoading={isLoading}
        />
        <TrendChartCard
          title={t("viewsAvgShort")}
          dataKey="viewsPerStream"
          color={explorerAccentAt(1)}
          tooltipLabel={t("viewsAvgShort")}
          granularityTooltip={t("granularity.week")}
          data={data}
          median={
            data.length > 0 && !isLoading
              ? calculateMedian(
                  data
                    .map((t) => t.viewsPerStream)
                    .filter((v): v is number => typeof v === "number"),
                )
              : undefined
          }
          formatValue={formatNumber}
          yAxisWidth={45}
          isLoading={isLoading}
        />
        <LivestreamViewerDualLineChart
          data={data}
          granularityTooltip={t("granularity.week")}
          isLoading={isLoading}
        />
      </Stack>
    </Box>
  );
};
